import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react'

import { formatMs } from './Swimlane'

import styles from './ChunkControls.module.scss'

import Button from '@/components/Button'
import type { SourceChunk } from '@/pages/app/AttributionViewer/sourceChunks'

interface Props {
  chunk?: SourceChunk
  index: number
  count: number
  canPrevious: boolean
  canNext: boolean
  onNavigate: (direction: -1 | 1) => void
  onPlay: () => void
  isPlaying: boolean
  audioUnavailable: boolean
  playOnSkip: boolean
  onPlayOnSkip: (value: boolean) => void
  previewMs: number | null
  onPreviewMs: (value: number | null) => void
  rate: number
  onRate: (value: number) => void
}

export const ChunkControls = (props: Props) => (
  <div className={styles.transport} aria-label="Chunk navigation">
    <div className={styles.controls}>
      <Button
        title={<ChevronLeft size={18} />}
        ariaLabel="Previous chunk"
        type="light"
        isDisabled={!props.canPrevious}
        onClick={() => props.onNavigate(-1)}
      />
      <Button
        title={props.isPlaying ? <Pause size={18} /> : <Play size={18} />}
        type="light"
        ariaLabel={props.isPlaying ? 'Pause audio' : 'Play chunk'}
        isDisabled={props.audioUnavailable || !props.chunk}
        onClick={props.onPlay}
      />
      <Button
        title={<ChevronRight size={18} />}
        ariaLabel="Next chunk"
        type="light"
        isDisabled={!props.canNext}
        onClick={() => props.onNavigate(1)}
      />
      <span className={styles.chunkLabel}>
        {props.chunk ? `Chunk ${props.index + 1} / ${props.count}` : 'No speech chunks'}
        {props.chunk && (
          <small>
            {formatMs(props.chunk.startMs)} – {formatMs(props.chunk.endMs)}
          </small>
        )}
      </span>
    </div>
    <div className={styles.controls}>
      <label className={styles.option}>
        <input
          type="checkbox"
          checked={props.playOnSkip}
          onChange={(event) => props.onPlayOnSkip(event.target.checked)}
        />
        Play on skip
      </label>
      <select
        aria-label="Audio preview length"
        value={props.previewMs ?? 'full'}
        onChange={(event) => props.onPreviewMs(event.target.value === 'full' ? null : Number(event.target.value))}
      >
        <option value={5000}>First 5 seconds</option>
        <option value="full">Full chunk</option>
      </select>
      <select
        aria-label="Playback speed"
        value={props.rate}
        onChange={(event) => props.onRate(Number(event.target.value))}
      >
        {[1, 1.25, 1.5, 2].map((rate) => (
          <option value={rate} key={rate}>
            {rate}×
          </option>
        ))}
      </select>
    </div>
    <small className={styles.shortcuts}>← → skip · Space play / pause</small>
  </div>
)
